import React, {useRef, useState, ReactNode} from 'react'
import clsx from 'clsx'
import styles from './UIGameFloatingText.module.scss'
import {TranslationObject} from '@colonist/i18n'
import {T, useUIGlobalContext} from '@colonist/ui'
import {createPortal} from 'react-dom'

type FloatingTextDirection = 'up' | 'down'

export interface UIGameFloatingTextProps {
    text: TranslationObject
    className?: string | undefined
    direction: FloatingTextDirection
    children: ReactNode
    disabled?: boolean
}

const FLOAT_DURATION = 1000

export const UIGameFloatingText = ({
    text,
    className,
    direction,
    children,
    disabled,
}: UIGameFloatingTextProps) => {
    const {isUserOnMobile} = useUIGlobalContext()
    const [floatings, setFloatings] = useState<number[]>([])
    const nextAvailableId = useRef(0)
    const containerRef = useRef<HTMLDivElement | null>(null)

    const handleClick = () => {
        if(disabled) return
        const currentId = nextAvailableId.current
        nextAvailableId.current = nextAvailableId.current + 1
        setFloatings((prev) => [...prev, currentId])
        setTimeout(() => {
            setFloatings((prev) => prev.filter((id) => id !== currentId))
        }, FLOAT_DURATION)
    }

    const container = containerRef.current
    const containterX = container?.getBoundingClientRect().x ?? 0
    const containterWidth = container?.getBoundingClientRect().width ?? 0
    const x = containterX + (containterWidth / 2)
    const y = container?.getBoundingClientRect().y ?? 0

    const FloatingTextComponent = isUserOnMobile ? FloatingTextMobile : FloatingText
    return (
        <div
            className={clsx(styles.container, className)}
            onClick={handleClick}
            ref={containerRef}
        >
            {children}
            {floatings.map((id) =>
                createPortal(<FloatingTextComponent
                    direction={direction}
                    text={text}
                    x={x}
                    y={y}
                />, document.body, id))}
        </div>
    )
}

interface FloatingTextProps {
    direction: FloatingTextDirection
    text: TranslationObject
    x: number
    y: number
}

const FloatingText = ({direction, text, x, y}: FloatingTextProps) => (
    <div className={clsx(styles.floatingText, styles[direction])} style={{
        animationDuration: `${FLOAT_DURATION}ms`,
        left: x,
        top: y,
    }}>
        <T id={text}/>
    </div>
)

const FloatingTextMobile = ({text}: FloatingTextProps) => (
    <div className={styles.floatingTextMobile} style={{
        animationDuration: `${FLOAT_DURATION}ms`,
    }}>
        <T id={text}/>
    </div>
)
